import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { fade, makeStyles } from '@material-ui/core/styles';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import NotificationsIcon from '@material-ui/icons/Notifications';

import {
  Badge,
  Avatar,
  Select,
  Tooltip,
  Toolbar,
  MenuItem,
  IconButton,
  FormControl
} from '@material-ui/core';

import { WILDCARD_ORG_ID } from '../lib/api/API';
import logo from '../images/blumira-logo-white.svg';

import TopbarView from './TopbarView';
import SearchableListDialog from './Components/SearchableListDialog'

const useStyles = makeStyles((theme) => ({
  title: {
    fontWeight: 700,
    margin: theme.spacing(1)
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 200,
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    backgroundColor: fade(theme.palette.common.white, 0.15),
    '&:hover': {
      backgroundColor: fade(theme.palette.common.white, 0.25),
    },
    transition: theme.transitions.create(
      ['background-color'],
      { duration: theme.transitions.duration.short }
    ),
  },
  toolbarActions: {
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: fade(theme.palette.common.white, 0.25),
    }
  },
  userAvatar: {
    backgroundColor: fade(theme.palette.common.white, 0.25),
    width: theme.spacing(3),
    height: theme.spacing(3),
  },
  userLogout: {
    margin:theme.spacing(1),
  },
  topbarNav: {
    flexGrow: 1,
  },
  orgDialogButton: {
    padding: 10,
    minWidth: 200,
    cursor: 'pointer',
    position: 'relative',
    margin: theme.spacing(1),
    border: '1px solid lightgray',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade(theme.palette.common.white, 0.15),
    '& p': {
      margin: 0,
    }
  }
}));

export const ADMIN_MODE_ORG = {
  id: WILDCARD_ORG_ID,
  name: 'Administer All Orgs',
};


const HeaderView = (props) => {
  const classes = useStyles();

  const [orgList, setOrgList] = useState([])
  const [isOrgDialogOpen, setIsOrgDialogOpen] = useState(false)

  const {
    first_name = '',
    last_name = '',
    picture = '',
    superadmin = false,
  } = props.session.settings.user;

  useEffect(() => {
    const newOrgList = superadmin ? [ADMIN_MODE_ORG, ...props.session.settings.userOrgs] : props.session.settings.userOrgs
    setOrgList(newOrgList)
  }, [props.session, props.session.settings.userOrgs, superadmin])

  const fullName = `${first_name || ''} ${last_name || ''}`;

  const routeToOrg = (orgId) => {
    const {
      routeToOrg,
      type,
      query = {},
    } = props;
    routeToOrg({ type, payload: { ...props.payload, orgId }, query: { ...query } });
  }

  const handleNotificationClick = () => {
    props.dispatch({
      type: 'PAGE',
      payload: {
        orgId: props.payload.orgId,
        toplevel: 'user',
        secondlevel: 'alertsettings',
      },
    });
  }

  const handleUserClick = () => {
    props.dispatch({
      type: 'PAGE',
      payload: {
        orgId: props.payload.orgId,
        toplevel: 'user',
        secondlevel: 'myaccount',
      },
    });
  }

  const toggleOrgDialog = () => {
    setIsOrgDialogOpen(!isOrgDialogOpen)
  }

  return (
    <Toolbar>
      <div>
        <img style={{ height: '32px' }} src={logo} alt='Logo'/>
      </div>
      <div className={classes.topbarNav}>
        {props.withNav &&
          <TopbarView toplevel={props.toplevel} secondlevel={props.secondlevel} dispatch={props.dispatch} orgId={props.orgId} />
        }
      </div>

      {(orgList.length > 0 && orgList.length <= 5) ? (
        <FormControl className={classes.formControl}>
          <Select
            className={classes.inputRoot}
            align="center"
            labelId="demo-simple-select-outlined-label"
            id="demo-simple-select-outlined"
            value={props.orgId}
            onChange={(e) => routeToOrg(e.target.value)}
            label="Organization"
            data-cy={'selectOrgBtn'}
          >
            {orgList.sort((a,b)=>a.name.localeCompare(b.name, 'en', { sensitivity: 'base' })).map((org) => <MenuItem key={org.id} value={org.id} data-cy={'selectOrgItem'}>{org.name}</MenuItem>)}
          </Select>
        </FormControl>
      ) : orgList.length > 5 ?
        <div className={classes.orgDialogButton} onClick={toggleOrgDialog} data-cy={'selectOrgBtn'}>
          <p>{orgList.find(({ id }) => id === props.orgId).name || ''}</p>
        </div>
      : null}

      <IconButton data-cy={'notificationSettingsBtn'} className={classes.toolbarActions} onClick={handleNotificationClick}>
        <Tooltip title="Notification Settings">
          <Badge badgeContent={0} color="secondary">
            <NotificationsIcon />
          </Badge>
        </Tooltip>
      </IconButton>
      
      <IconButton data-cy={'accountSettingsBtn'} className={classes.toolbarActions} onClick={handleUserClick}>
        <Tooltip title="Account Settings">
          <Avatar alt={fullName} src={picture} className={classes.userAvatar} />
        </Tooltip>
      </IconButton>

      <IconButton data-cy={'logoutBtn'} className={classes.toolbarActions} onClick={props.routeToLogin}>
        <Tooltip title="Logout">
          <ExitToAppIcon />
        </Tooltip>
      </IconButton>
      <SearchableListDialog 
        open={isOrgDialogOpen} 
        onItemClick={routeToOrg}
        onClose={toggleOrgDialog}
        title={'Switch organizations'}
        items={orgList.sort((a,b)=>a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }))}
      />
    </Toolbar>

  );
}

HeaderView.propTypes = {
  type: PropTypes.string.isRequired,
  payload: PropTypes.shape({}),
  query: PropTypes.shape({}),
  routeToLogin: PropTypes.func.isRequired,
  routeToOrg: PropTypes.func.isRequired,
};

HeaderView.defaultProps = {
  payload: {},
  query: {},
};

const mapStateToProps = (state) => {
  const { session = {} } = state
  return {
    session: session
  }
};
export default connect(mapStateToProps)(HeaderView);
